import React, { useState } from 'react';
import { LucideIcon, AlertTriangle, CheckCircle, ArrowRight, Info, HelpCircle } from 'lucide-react';
import FooterAction from './FooterAction';
import DeepDiveModal from './DeepDiveModal';

// 定义新的、符合策略的数据结构
interface FailAdvice {
  id: string;
  title: string;
  category: 'fail';
  isPrioritized: boolean;
  communityIntervention: {
    content: string;
  };
  actionSteps: string[];
  deepDive: {
    content: string;
    knowledgeBlocks?: (string | null)[];
  };
  professionalReferral: {
    content: string;
    urgency: string;
  };
}

interface PassAdvice {
  id: string;
  title: string;
  category: 'pass';
  content: string;
  isPrioritized: boolean;
  nextReassessment?: string;
}

// 定义组件的Props
interface SpecificAdviceCardProps {
  advice: FailAdvice | PassAdvice;
  icon: LucideIcon;
  colorConfig: {
    bg: string;
    border: string;
    text: string;
    iconBg: string;
    iconText: string;
    bullet: string;
  };
  onPrioritize: (id: string) => void; // 新增回调函数
}

// 卡片组件 (已升级)
const SpecificAdviceCard: React.FC<SpecificAdviceCardProps> = ({ advice, icon: Icon, colorConfig, onPrioritize }) => {
  const [showDeepDive, setShowDeepDive] = useState(false);
  const cardBaseStyle = `border-2 rounded-2xl p-6 flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-full`;

  // --- 渲染 "保持良好" (PASS) 的卡片 - 匹配截图样式 ---
  const renderPassCard = (passAdvice: PassAdvice) => (
    <>
      <div className="flex-1">
        {/* 您的情况 - 简洁的单一内容区域 */}
        <div className="p-6 rounded-2xl bg-white/70 shadow-sm border border-white/50">
          <div className="flex items-start space-x-4 mb-4">
            <div className={`p-2 rounded-full bg-gray-100`}>
              <Info className="w-6 h-6 text-gray-600" />
            </div>
            <div className="flex-1">
              <h5 className={`font-bold text-lg mb-4 ${colorConfig.text}`}>
                您的情况
              </h5>
              <div className={`text-base leading-relaxed ${colorConfig.text} space-y-4`}>
                {/* 解析内容中的编号列表 */}
                {passAdvice.content.split('\n').map((line, index) => {
                  const trimmedLine = line.trim();
                  if (!trimmedLine) return null;
                  
                  // 检查是否是编号列表项
                  const numberMatch = trimmedLine.match(/^(\d+)\s*[..。]\s*(.*)/);
                  if (numberMatch) {
                    return (
                      <div key={index} className="flex items-start space-x-3">
                        <span className={`font-semibold ${colorConfig.text}`}>
                          {numberMatch[1]}.
                        </span>
                        <span className="flex-1">{numberMatch[2]}</span>
                      </div>
                    );
                  }
                  
                  // 普通段落
                  return (
                    <p key={index} className="mb-3">
                      {trimmedLine}
                    </p>
                  );
                }).filter(Boolean)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // --- 渲染 "优先关注" (FAIL) 的卡片 - 匹配截图样式 ---
  const renderFailCard = (failAdvice: FailAdvice) => (
    <>
      <div className="space-y-6 flex-1 mb-6">
        {/* 1. 社区干预建议 - 匹配截图中的"社区干预"部分 */}
        <div className="p-5 rounded-2xl bg-white/70 shadow-sm border border-white/50">
          <div className="flex items-start space-x-3 mb-4">
            <div className={`p-2 rounded-full ${colorConfig.iconBg}`}>
              <HelpCircle className={`w-5 h-5 ${colorConfig.iconText}`} />
            </div>
            <div className="flex-1">
              <h5 className={`font-bold text-lg mb-3 ${colorConfig.text}`}>
                社区干预:
              </h5>
              <p className={`text-base leading-relaxed ${colorConfig.text}`}>
                {failAdvice.communityIntervention.content}
              </p>
            </div>
          </div>
        </div>

        {/* 2. 行动步骤 - 匹配截图中的"行动步骤"部分 */}
        <div className="p-5 rounded-2xl bg-white/70 shadow-sm border border-white/50">
          <div className="flex items-start space-x-3 mb-4">
            <div className={`p-2 rounded-full ${colorConfig.iconBg}`}>
              <CheckCircle className={`w-5 h-5 ${colorConfig.iconText}`} />
            </div>
            <div className="flex-1">
              <h5 className={`font-bold text-lg mb-4 ${colorConfig.text}`}>
                行动步骤
              </h5>
              <div className="space-y-3">
                {failAdvice.actionSteps.map((step, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <ArrowRight className={`w-4 h-4 mt-0.5 flex-shrink-0 ${colorConfig.iconText}`} />
                    <p className={`flex-1 text-base ${colorConfig.text}`}>{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. 专业转介 - 匹配截图中的"专业转介"部分 */}
      <div className={`border-t-2 ${colorConfig.border} pt-6 mt-auto`}>
        <div className="p-5 rounded-2xl bg-white/80 shadow-sm border border-white/50">
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-full bg-red-100`}>
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h5 className={`font-bold text-lg mb-3 ${colorConfig.text}`}>
                专业转介:
              </h5>
              <p className={`text-base leading-relaxed mb-4 ${colorConfig.text}`}>
                {failAdvice.professionalReferral.content}
              </p>
              
              {/* 添加FooterAction组件 - 查看深度指导 */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <FooterAction
                  text="查看深度指导"
                  onClick={() => setShowDeepDive(true)}
                  variant="secondary"
                  className="w-full justify-center"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className={`${cardBaseStyle} ${colorConfig.bg} ${colorConfig.border}`}>
      {/* 头部：图标、标题和优先按钮 */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className={`p-4 rounded-full shadow-sm ${colorConfig.iconBg}`}>
            <Icon className={`w-8 h-8 ${colorConfig.iconText}`} />
          </div>
          <h4 className={`font-bold text-2xl leading-relaxed ${colorConfig.text}`}>
            {advice.title}
          </h4>
        </div>
        {advice.category === 'fail' && (
          <button 
            onClick={() => onPrioritize(advice.id)}
            className={`px-4 py-2 text-sm font-medium rounded-full shadow-sm transition-colors flex items-center space-x-2 ${
              advice.isPrioritized 
                ? 'bg-yellow-500 text-white' 
                : `bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-300`
            }`}
          >
            <AlertTriangle className="w-4 h-4" />
            <span>{advice.isPrioritized ? '已优先处理' : '我想优先处理'}</span>
          </button>
        )}
      </div>

      {/* 根据 category 渲染不同内容的卡片 */}
      {advice.category === 'fail' ? renderFailCard(advice) : renderPassCard(advice)}
      
      {/* 深度指导模态窗口 */}
      {advice.category === 'fail' && (
        <DeepDiveModal
          isOpen={showDeepDive}
          onClose={() => setShowDeepDive(false)}
          title={advice.title}
          content={advice.deepDive.content}
          knowledgeBlocks={advice.deepDive.knowledgeBlocks}
        />
      )}
    </div>
  );
};

export default SpecificAdviceCard;